import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Layout,
	Card,
	Table,
	Button,
	Space,
	Modal,
	Form,
	Input,
	Switch,
	message,
	Tag,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";

import PageTitle from "../../../PageTitle";
import useAxios from "../../../../utilities/useAxios";
import AccessedCarNavigation from "../AccessedCarNavigation";
import PageHeader from "../../../common/PageHeader";

const { Content } = Layout;

const API_LIST = "/utils/rental/car/types/";

const ViewCarTypes = () => {
	const [list, setList] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [searchText, setSearchText] = useState("");
	const [form] = Form.useForm();

	const filteredList = useMemo(() => {
		const keyword = searchText.trim().toLowerCase();
		if (!keyword) {
			return list;
		}
		return list.filter((row) =>
			String(row.name || "")
				.toLowerCase()
				.includes(keyword),
		);
	}, [list, searchText]);

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const res = await useAxios.get(API_LIST);
			if (res.status === 200) {
				setList(res.data.car_types || []);
			}
		} catch {
			message.error("차량 목록을 불러오지 못했습니다.");
			setList([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const openCreate = () => {
		setEditing(null);
		form.resetFields();
		form.setFieldsValue({
			color: "#2457e6",
			can_rent: true,
		});
		setModalOpen(true);
	};

	const openEdit = (record) => {
		setEditing(record);
		form.setFieldsValue({
			name: record.name,
			color: record.color || "#2457e6",
			can_rent: !!record.can_rent,
		});
		setModalOpen(true);
	};

	const handleSubmit = async (values) => {
		const payload = {
			name: values.name?.trim(),
			color: values.color || "#2457e6",
			can_rent: !!values.can_rent,
		};
		if (!payload.name) {
			message.error("차량 이름을 입력하세요.");
			return;
		}
		try {
			setLoading(true);
			if (editing) {
				const res = await useAxios.put(
					`${API_LIST}${editing.id}/`,
					payload,
				);
				if (res.status === 200) {
					message.success(
						"차량 정보를 수정했습니다.",
					);
					setModalOpen(false);
					setEditing(null);
					form.resetFields();
					loadData();
				}
			} else {
				const res = await useAxios.post(
					API_LIST,
					payload,
				);
				if (res.status === 201) {
					message.success(
						"차량 정보를 등록했습니다.",
					);
					setModalOpen(false);
					form.resetFields();
					loadData();
				}
			}
		} catch (error) {
			const errMsg =
				error?.response?.data?.message ||
				(editing
					? "차량 정보 수정에 실패했습니다."
					: "차량 정보 등록에 실패했습니다.");
			message.error(errMsg);
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "색상",
			dataIndex: "color",
			key: "color",
			render: (color) => (
				<Space>
					<span
						style={{
							display: "inline-block",
							width: 16,
							height: 16,
							borderRadius: 4,
							background:
								color ||
								"#2457e6",
							border: "1px solid #d9d9d9",
						}}
					/>
					{color || "-"}
				</Space>
			),
		},
		{
			title: "대여 가능",
			dataIndex: "can_rent",
			key: "can_rent",
			render: (v) =>
				v ? (
					<Tag color="green">가능</Tag>
				) : (
					<Tag>불가</Tag>
				),
		},
		{
			title: "관리",
			key: "actions",
			render: (_, record) => (
				<Button
					type="primary"
					size="small"
					icon={<EditOutlined />}
					onClick={() => openEdit(record)}>
					수정
				</Button>
			),
		},
	];

	return (
		<>
			<PageTitle title="차량 목록 관리" />
			<Layout className="member-layout">
				<AccessedCarNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="utils-rent_sanctuary__container page">
							<PageHeader
								title="차량 목록 관리"
								actions={
									<>
										<Button
									type="primary"
									icon={
										<PlusOutlined />
									}
									onClick={
										openCreate
									}>
									차량
									추가
								</Button>
									</>
								}
							/>
							<Card
								className="surface-card"
								style={{
									width: "100%",
									minWidth: 0,
								}}>
								<Input.Search
									allowClear
									placeholder="차량번호 검색"
									style={{
										maxWidth: 280,
										marginBottom: 16,
									}}
									value={searchText}
									onChange={(e) =>
										setSearchText(
											e.target
												.value,
										)
									}
								/>
								<Table
									rowKey="id"
									loading={
										loading
									}
									columns={
										columns
									}
									dataSource={
										filteredList
									}
									pagination={{
										pageSize: 20,
									}}
									scroll={{
										x: "max-content",
									}}
									sticky={{
										offsetHeader: 64,
									}}
								/>
							</Card>
						</div>
					</Content>
				</Layout>
			</Layout>

			<Modal
				title={
					editing
						? "차량 정보 수정"
						: "차량 정보 추가"
				}
				open={modalOpen}
				onCancel={() => {
					setModalOpen(false);
					setEditing(null);
					form.resetFields();
				}}
				footer={null}
				destroyOnClose
				width={480}>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}>
					<Form.Item
						name="name"
						label="이름"
						rules={[
							{
								required: true,
								message: "이름을 입력하세요.",
							},
						]}>
						<Input placeholder="차량 이름" />
					</Form.Item>
					<Form.Item
						name="color"
						label="색상"
						rules={[
							{
								required: true,
								message: "색상을 입력하세요.",
							},
							{
								pattern: /^#[0-9A-Fa-f]{6}$/,
								message: "Hex 색상 코드 형식(#RRGGBB)으로 입력하세요.",
							},
						]}>
						<Input
							type="color"
							style={{
								width: 64,
								padding: 2,
								height: 40,
							}}
						/>
					</Form.Item>
					<Form.Item
						name="can_rent"
						label="대여 가능"
						valuePropName="checked">
						<Switch
							checkedChildren="가능"
							unCheckedChildren="불가"
						/>
					</Form.Item>
					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
							block>
							{editing
								? "저장"
								: "등록"}
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default ViewCarTypes;
